import { Suspense, lazy, useEffect, useRef, useState, type ReactNode } from 'react';
import { NavLink, Navigate, Route, Routes, useLocation } from 'react-router-dom';
import { api } from './api/client.js';
import { AmbientBg } from './components/AmbientBg.js';
import { initMusic } from './store/music.js';
import { Conga } from './components/Conga.js';
import { BotReportsNotice } from './components/BotReportsNotice.js';
import { HeaderLogo, MenuIcon, MoonIcon, Splash, SunIcon, Toasts } from './components/ui.js';
import { MobileMenu } from './components/MobileMenu.js';
import { useNavItems, isMobileTab, markSpaceNav, navSection } from './components/nav.js';
import { ThemeStyles } from './components/ThemeStyles.js';
import { usePhone } from './hooks/useMedia.js';
import { LoginPage } from './pages/LoginPage.js';
import { UnsavedLeave } from './pages/settings/UnsavedLeave.js';
import { TablePage } from './pages/TablePage.js';

/**
 * Таблиця відкривається першою, тож вона в основному пакунку. Решта сторінок —
 * люди, рапорти, налаштування, кабінет курсанта — важать більше за неї разом
 * узяті й потрібні не щоразу, тому вантажимо їх тоді, коли туди заходять:
 * перший показ таблиці не має чекати на код, якого ще не просили.
 */
const MePage = lazy(async () => ({ default: (await import('./pages/MePage.js')).MePage }));
const PeoplePage = lazy(async () => ({
  default: (await import('./pages/PeoplePage.js')).PeoplePage,
}));
const ReportsPage = lazy(async () => ({
  default: (await import('./pages/ReportsPage.js')).ReportsPage,
}));
const SettingsPage = lazy(async () => ({
  default: (await import('./pages/settings/SettingsPage.js')).SettingsPage,
}));
const UpdatesModal = lazy(async () => ({
  default: (await import('./components/modals/UpdatesModal.js')).UpdatesModal,
}));
const ProgramPage = lazy(async () => ({
  default: (await import('./pages/settings/SettingsPage.js')).ProgramPage,
}));
import { useCan, useData } from './store/data.js';
import { useLink } from './store/link.js';
import { useLive } from './store/live.js';
import { useMarks } from './store/marks.js';
import { themeOriginFromClick, useUi } from './store/ui.js';

/** Розділи з таблицею на весь екран і скляною панеллю над нею. */
const TABLE_SECTIONS = new Set(['', 'duty', 'away', 'leave', 'people']);

function TopBar(): ReactNode {
  const { session, orgs, logout, switchOrg } = useData();
  const theme = useUi(s => s.theme);
  const setTheme = useUi(s => s.setTheme);
  const canOrgs = useCan('orgs.manage');
  const navItems = useNavItems();
  const phone = usePhone();
  const { pathname } = useLocation();
  // Чотири сталі вкладки; решта розділів доступна через меню.
  const tabs = phone ? navItems.filter(isMobileTab) : navItems;
  const menu = useUi(s => s.menu);
  const setMenu = useUi(s => s.setMenu);
  const updates = useUi(s => s.updates);
  const setUpdates = useUi(s => s.setUpdates);

  /*
   * Після оновлення програми вікно «Оновлення» відкривається само — один раз.
   * Що людина вже бачила, знає сервер; без зв'язку нічого не відкриваємо.
   * Підпис беремо з окремого маленького модуля, щоб не тягнути сюди вікно.
   */
  const userId = session?.user.id;
  useEffect(() => {
    if (!userId) return;
    let cancel = false;
    void (async () => {
      try {
        const { LATEST_UPDATE } = await import('./components/modals/updates-content.js');
        if (!LATEST_UPDATE) return;
        const { seen } = await api.get<{ seen: string | null }>('/api/updates/seen');
        if (cancel || seen === LATEST_UPDATE) return;
        setUpdates(true);
        await api.post('/api/updates/seen', { mark: LATEST_UPDATE });
      } catch {
        /* немає зв'язку — покажемо наступного разу */
      }
    })();
    return () => {
      cancel = true;
    };
  }, [userId, setUpdates]);
  /*
   * Меню одне на всі сторінки, а от вміст у нього різний: таблиця додає до
   * нього «Вигляд» і «Дії», тому там його малює сама сторінка. Кнопка ж
   * лишається спільною — у шапці, поруч зі знаком курсу.
   */
  const tableOwnsMenu = ['/', '/duty', '/away', '/leave'].includes(pathname);
  const [accountPop, setAccountPop] = useState(false);
  const accountBox = useRef<HTMLDivElement>(null);
  const headerEl = useRef<HTMLElement>(null);

  // Висота шапки потрібна шару фону в налаштуваннях: він має починатися від
  // верху вікна, щоб плями під шапкою й під сторінкою були одним малюнком.
  useEffect(() => {
    const el = headerEl.current;
    if (!el) return;
    const apply = (): void => {
      document.documentElement.style.setProperty('--header-h', `${el.offsetHeight}px`);
    };
    apply();
    const ro = new ResizeObserver(apply);
    ro.observe(el);
    return () => {
      ro.disconnect();
      document.documentElement.style.removeProperty('--header-h');
    };
  }, []);

  useEffect(() => {
    if (!accountPop) return;
    const close = (e: MouseEvent): void => {
      if (!accountBox.current?.contains(e.target as Node)) setAccountPop(false);
    };
    document.addEventListener('mousedown', close);
    return () => document.removeEventListener('mousedown', close);
  }, [accountPop]);

  return (
    <header className="app-header" ref={headerEl}>
      {/* Назва продукту зліва, знак курсу — праворуч. */}
      <div className="brand">
        <HeaderLogo />
      </div>
      <nav aria-label="Основні розділи">
        {tabs.map(i => (
          <NavLink
            key={i.to}
            to={i.to}
            end={i.end}
            viewTransition
            title={i.title ?? i.label}
            aria-label={i.title ?? i.label}
            className={({ isActive }) => `nav-link ${isActive ? 'active' : ''}`}
            data-label={phone ? (i.short ?? i.label) : i.label}
            onClick={() => markSpaceNav(pathname, i.to)}
          >
            {phone ? (i.short ?? i.label) : i.label}
          </NavLink>
        ))}
      </nav>
      <div className="spacer" style={{ flex: 1 }} />
      {phone ? (
        <button
          type="button"
          className="header-tool icon burger"
          aria-label="Меню"
          aria-haspopup="dialog"
          aria-expanded={menu}
          onClick={() => setMenu(true)}
        >
          <MenuIcon />
        </button>
      ) : null}
      {menu && !tableOwnsMenu ? <MobileMenu onClose={() => setMenu(false)} /> : null}
      {updates ? (
        <Suspense fallback={null}>
          <UpdatesModal onClose={() => setUpdates(false)} />
        </Suspense>
      ) : null}
      <div className="header-account">
        <button
          type="button"
          className="header-tool icon"
          title="Оновлення"
          aria-label="Оновлення"
          aria-haspopup="dialog"
          onClick={() => setUpdates(true)}
        >
          <UpdatesIcon />
        </button>
        <button
          type="button"
          className="header-tool icon"
          title="Тема оформлення"
          onClick={e => setTheme(theme === 'dark' ? 'light' : 'dark', themeOriginFromClick(e))}
        >
          {theme === 'dark' ? <MoonIcon /> : <SunIcon />}
        </button>
        <Presence />
        <LinkLamp />
      </div>
      <div className="viewmenu" ref={accountBox}>
        <button
          type="button"
          className="brand-mark"
          title={session?.org.shortName}
          aria-expanded={accountPop}
          aria-haspopup="menu"
          onClick={() => setAccountPop(v => !v)}
        >
          {session?.org.shortName || 'С2'}
        </button>
        {accountPop ? (
          <div className="vpop right" role="menu">
            {/* Курси — тут же, під знаком поточного: окремий вибір біля назви
                продукту займав місце в шапці заради дії, якою користуються
                раз на день. Поточний курс позначений і нікуди не веде. */}
            {canOrgs && orgs.length > 1
              ? orgs.map(o => (
                  <button
                    key={o.id}
                    type="button"
                    className={`vpop-row${o.id === session?.org.id ? ' on' : ''}`}
                    role="menuitemradio"
                    aria-checked={o.id === session?.org.id}
                    onClick={() => {
                      setAccountPop(false);
                      if (o.id !== session?.org.id) void switchOrg(o.id);
                    }}
                  >
                    {o.shortName}
                    <span className="vpop-count">{o.people}</span>
                  </button>
                ))
              : null}
            {canOrgs && orgs.length > 1 ? <div className="vpop-sep" /> : null}
            <button
              type="button"
              className="vpop-row"
              role="menuitem"
              onClick={() => {
                setAccountPop(false);
                void logout();
              }}
            >
              Вийти
            </button>
          </div>
        ) : null}
      </div>
    </header>
  );
}

/** Стрілка по колу — журнал оновлень. */
function UpdatesIcon(): ReactNode {
  return (
    <svg
      viewBox="0 0 24 24"
      width="17"
      height="17"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.9"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
    >
      <path d="M20 12a8 8 0 1 1-2.34-5.66" />
      <path d="M20 4v5h-5" />
    </svg>
  );
}

/** Силует людини для кнопки «хто в програмі». */
function PersonIcon(): ReactNode {
  return (
    <svg
      viewBox="0 0 24 24"
      width="17"
      height="17"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.9"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
    >
      <circle cx="12" cy="8" r="4" />
      <path d="M4.5 20.5c.8-3.6 3.9-5.5 7.5-5.5s6.7 1.9 7.5 5.5" />
    </svg>
  );
}

/** «з 14:02» — коли людина відкрила першу вкладку. */
const sinceLabel = (iso: string): string => {
  const at = new Date(iso);
  const sameDay = at.toDateString() === new Date().toDateString();
  const time = at.toLocaleTimeString('uk-UA', { hour: '2-digit', minute: '2-digit' });
  return sameDay ? `з ${time}` : `з ${at.toLocaleDateString('uk-UA', { day: 'numeric', month: 'short' })} ${time}`;
};

/**
 * Хто зараз у цьому курсі. Список живе в каналі подій: відкрита вкладка —
 * людина в списку, закрилася — зникла. Число на кнопці рахує людей, а не
 * вкладки, і не показується, коли в курсі лише ви. Інший курс — інший
 * список: канал підключається до того курсу, у якому працює вкладка.
 */
function Presence(): ReactNode {
  const online = useLive(s => s.online);
  const connected = useLive(s => s.connected);
  const me = useData(s => s.session?.user.id);
  const [open, setOpen] = useState(false);
  const box = useRef<HTMLDivElement>(null);
  const others = online.filter(u => u.userId !== me).length;
  const title = !connected
    ? 'Немає зв’язку з сервером'
    : others === 0
      ? 'У програмі лише ви'
      : others === 1
        ? 'У програмі ще одна людина'
        : `У програмі ще ${others} людей`;

  useEffect(() => {
    if (!open) return;
    // Список живий, але на відкритті все одно перепитуємо: раптом подія
    // про присутність загубилася разом із каналом.
    void useLive.getState().fetchPresence();
    const close = (e: MouseEvent): void => {
      if (!box.current?.contains(e.target as Node)) setOpen(false);
    };
    const esc = (e: KeyboardEvent): void => {
      if (e.key === 'Escape') setOpen(false);
    };
    document.addEventListener('mousedown', close);
    document.addEventListener('keydown', esc);
    return () => {
      document.removeEventListener('mousedown', close);
      document.removeEventListener('keydown', esc);
    };
  }, [open]);

  return (
    <div className="viewmenu" ref={box}>
      <button
        type="button"
        className={`header-tool icon presence-btn${others ? ' has-others' : ''}`}
        title={title}
        aria-label={title}
        aria-expanded={open}
        aria-haspopup="dialog"
        onClick={() => setOpen(v => !v)}
      >
        <PersonIcon />
        {others ? <span className="presence-count">{others}</span> : null}
      </button>
      {open ? (
        <div className="vpop right link-pop presence-pop" role="dialog" aria-label="Хто в програмі">
          <div className="link-pop-head">
            <b>{connected ? `У програмі: ${online.length}` : 'Немає зв’язку'}</b>
          </div>
          {online.map(u => (
            <div key={u.userId} className="presence-row">
              <span className="presence-dot" />
              <span className="presence-name">
                {u.fullName || u.login}
                {u.userId === me ? <span className="presence-me">, це ви</span> : null}
              </span>
              <span className="presence-meta">
                {u.login}, {sinceLabel(u.since)}
              </span>
            </div>
          ))}
          {connected && !online.length ? (
            <div className="link-pop-row">
              <span>Список ще завантажується…</span>
            </div>
          ) : null}
        </div>
      ) : null}
    </div>
  );
}

/** «3 дн 4 год 12 хв» — скільки сервер уже працює без перезапуску. */
function formatUptime(sec: number): string {
  const d = Math.floor(sec / 86400);
  const h = Math.floor((sec % 86400) / 3600);
  const m = Math.floor((sec % 3600) / 60);
  if (d) return `${d} дн ${h} год`;
  if (h) return `${h} год ${m} хв`;
  if (m) return `${m} хв`;
  return `${sec} с`;
}

const clock = (ms: number): string =>
  new Date(ms).toLocaleTimeString('uk-UA', { hour: '2-digit', minute: '2-digit', second: '2-digit' });

/**
 * Лампочка зв'язку. Сама вона каже тільки «є/немає», а по клацанню
 * відкриває картку: пінг, стан бази, скільки сервер працює і що чекає в
 * черзі. Відкриття одразу перепитує сервер — щоб цифри були свіжі, а не з
 * попереднього такту.
 */
function LinkLamp(): ReactNode {
  const online = useLink(s => s.online);
  const syncing = useLink(s => s.syncing);
  const pending = useLink(s => s.outbox + s.marksPending);
  const pingMs = useLink(s => s.pingMs);
  const pingAt = useLink(s => s.pingAt);
  const server = useLink(s => s.server);
  const [open, setOpen] = useState(false);
  const [checking, setChecking] = useState(false);
  const box = useRef<HTMLDivElement>(null);
  const mode = !online ? 'off' : syncing || pending ? 'sync' : 'on';
  const title = !online
    ? pending
      ? `Немає зв’язку з сервером, ${pending} змін чекають надіслання`
      : 'Немає зв’язку з сервером'
    : pending
      ? 'Надсилаю збережені зміни…'
      : pingMs === null
        ? 'Сервер підключено'
        : `Сервер підключено, ${pingMs} мс`;

  useEffect(() => {
    if (!open) return;
    const close = (e: MouseEvent): void => {
      if (!box.current?.contains(e.target as Node)) setOpen(false);
    };
    const esc = (e: KeyboardEvent): void => {
      if (e.key === 'Escape') setOpen(false);
    };
    document.addEventListener('mousedown', close);
    document.addEventListener('keydown', esc);
    return () => {
      document.removeEventListener('mousedown', close);
      document.removeEventListener('keydown', esc);
    };
  }, [open]);

  const check = (): void => {
    if (checking) return;
    setChecking(true);
    void useLink
      .getState()
      .ping(true)
      .then(ok => {
        if (ok) {
          void useMarks.getState().flush();
          void useData.getState().refresh();
        }
      })
      .finally(() => setChecking(false));
  };

  const state = !online
    ? 'Немає зв’язку'
    : server && !server.db
      ? 'База даних не відповідає'
      : syncing
        ? 'Надсилаю зміни…'
        : 'Працює';
  const ping = pingMs === null ? '—' : online ? `${pingMs} мс` : `немає відповіді (${pingMs} мс)`;
  // Без зв'язку цифри з попередньої відповіді вже нічого не кажуть.
  const live = online ? server : null;

  return (
    <div className="viewmenu" ref={box}>
      <button
        type="button"
        className="header-tool icon"
        title={title}
        aria-label={title}
        aria-expanded={open}
        aria-haspopup="dialog"
        onClick={() => {
          setOpen(v => !v);
          if (!open) check();
        }}
      >
        <span className={`link-lamp ${mode}`} />
      </button>
      {open ? (
        <div className="vpop right link-pop" role="dialog" aria-label="Стан сервера">
          <div className="link-pop-head">
            <span className={`link-lamp ${mode}`} />
            <b>{state}</b>
          </div>
          <div className="link-pop-row">
            <span>Пінг</span>
            <b>{ping}</b>
          </div>
          <div className="link-pop-row">
            <span>База даних</span>
            <b>{!live ? '—' : live.db ? 'працює' : 'помилка'}</b>
          </div>
          <div className="link-pop-row">
            <span>Сервер працює</span>
            <b>{live ? formatUptime(live.uptime) : '—'}</b>
          </div>
          <div className="link-pop-row">
            <span>Час на сервері</span>
            <b>{live?.time ? clock(Date.parse(live.time)) : '—'}</b>
          </div>
          <div className="link-pop-row">
            <span>У черзі змін</span>
            <b>{pending}</b>
          </div>
          <div className="link-pop-row">
            <span>Перевірено</span>
            <b>{pingAt === null ? '—' : clock(pingAt)}</b>
          </div>
          <div className="vpop-sep" />
          <button type="button" className="vpop-row" onClick={check} disabled={checking}>
            {checking ? 'Перевіряю…' : 'Перевірити зараз'}
          </button>
        </div>
      ) : null}
    </div>
  );
}

/**
 * Заставка на старті тримається щонайменше стільки, навіть якщо сесія
 * піднялася швидше.
 *
 * Оновлення сторінки інакше виглядає як збій: екран білішає, блимає порожня
 * розкладка й одразу з'являється таблиця. За ці дві секунди знак встигає
 * обернутися, і перезавантаження читається як початок роботи, а не як ривок.
 */
const SPLASH_MS = 2000;

function useOpeningSplash(): boolean {
  const [opening, setOpening] = useState(true);
  useEffect(() => {
    const t = setTimeout(() => setOpening(false), SPLASH_MS);
    return () => clearTimeout(t);
  }, []);
  return opening;
}

export function App(): ReactNode {
  const status = useData(s => s.status);
  const load = useData(s => s.load);
  const opening = useOpeningSplash();
  const palettes = useData(s => s.session?.org.settings.palettes);
  const accent = useData(s => s.session?.org.settings.accent ?? '');
  const orgId = useData(s => s.session?.org.id);
  const location = useLocation();
  const section = navSection(location.pathname);

  useEffect(() => {
    initMusic();
  }, []);

  useEffect(() => {
    void load();
    const stopWatch = useLink.getState().watch();
    const stopSub = useLink.subscribe((s, prev) => {
      if (!(s.online && !prev.online)) return;
      const st = useData.getState().status;
      if (st === 'down') void useData.getState().load();
      else if (st === 'ready') {
        void useData.getState().refresh();
        void useMarks.getState().flush();
        void useLink.getState().flush();
      }
    });
    // Решту сторінок підвантажуємо, коли браузер звільнився: перший показ
    // таблиці їх не чекає, але й перехід на вкладку не впирається в мережу.
    const idle = window.requestIdleCallback ?? ((fn: () => void) => setTimeout(fn, 1200));
    const warm = idle(() => {
      void import('./pages/PeoplePage.js');
      void import('./pages/ReportsPage.js');
      void import('./pages/settings/SettingsPage.js');
    });

    return () => {
      stopWatch();
      stopSub();
      (window.cancelIdleCallback ?? clearTimeout)(warm as number);
    };
  }, [load]);

  // Канал подій живе, поки є сесія, і перевідкривається разом зі зміною
  // курсу: сервер розсилає події по курсу, у якому вкладка підключилася.
  useEffect(() => {
    if (status !== 'ready' || !orgId) return;
    return useLive.getState().connect();
  }, [status, orgId]);

  // Заставка перекриває все, зокрема й кабінет курсанта: це перше, що видно
  // після оновлення сторінки, і воно однакове на будь-якій адресі.
  const ambient = (
    <>
      <AmbientBg />
      <Conga />
    </>
  );
  /*
   * Другий шар — лише в налаштуваннях: там .app-main прокручується сам, а
   * backdrop-filter у Chrome усередині scroller бачить тільки його вміст.
   * На таблицях .app-main — окремий шар Spaces-переходу (view-transition-name),
   * і скло панелі над сіткою теж не бачить кореневого фону: ASCII проступав
   * крізь неї різким. Тож і там свій шар — непрозорий і зсунутий на висоту
   * шапки, тому з кореневим збігається піксель у піксель.
   * На решті сторінок скло бачить кореневий шар, і другий лише подвоював би
   * яскравість під контентом — шапка тоді читалася б як окрема смуга.
   */
  const ambientApp =
    section === 'settings' || TABLE_SECTIONS.has(section) ? <AmbientBg layer="app" /> : null;

  if (opening) {
    return (
      <>
        {ambient}
        <Splash />
      </>
    );
  }

  // Сторінка курсанта живе поза кабінетом: свій вхід, своя половина API,
  // жодної шапки з таблицями. Тому вона стоїть перед перевіркою сесії
  // діловода — інакше людину зустрічав би чужий логін.
  if (location.pathname.startsWith('/me')) {
    return (
      <>
        {ambient}
        <ThemeStyles palettes={palettes} accent={accent} />
        <Suspense fallback={<Splash delayed />}>
          <MePage />
        </Suspense>
        <Toasts />
      </>
    );
  }

  if (status === 'loading') {
    return (
      <>
        {ambient}
        <Splash />
      </>
    );
  }
  if (status === 'down') {
    return (
      <>
        {ambient}
        <Splash note="Немає зв’язку з сервером. Чекаю відновлення…" />
        <Toasts />
      </>
    );
  }
  if (status === 'anon') {
    return (
      <>
        {ambient}
        <LoginPage />
        <Toasts />
      </>
    );
  }

  return (
    <div className="app">
      {ambient}
      <ThemeStyles palettes={palettes} accent={accent} />
      <TopBar />
      {/* Курс у ключі: після перемикання сторінки збираються заново й самі
          перечитують свої дані — позначки, рапорти, налаштування. */}
      <main className="app-main" key={`${orgId}:${section}`}>
        {ambientApp}
        <Suspense fallback={<Splash delayed />}>
          <Routes location={location}>
            <Route path="/" element={<TablePage mode="meals" />} />
            <Route path="/duty" element={<TablePage mode="duty" />} />
            <Route path="/away" element={<TablePage mode="away" />} />
            <Route path="/leave" element={<TablePage mode="reports" />} />
            <Route path="/people" element={<PeoplePage />} />
            <Route path="/reports" element={<ReportsPage />} />
            <Route path="/settings/*" element={<SettingsPage />} />
            <Route path="/program/*" element={<ProgramPage />} />
            <Route path="*" element={<Navigate to="/" replace />} />
          </Routes>
        </Suspense>
      </main>
      <Toasts>
        <BotReportsNotice />
      </Toasts>
      <UnsavedLeave />
    </div>
  );
}
